import type { CSSProperties, ReactNode } from "react";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";

import styles from "./index.module.css";

type Placement = "right" | "left" | "top" | "bottom";

export interface TooltipProps {
  content: string;
  placement?: Placement;
  children: ReactNode;
}

const GAP = 6;

function getPopupStyle(rect: DOMRect, placement: Placement): CSSProperties {
  const base: CSSProperties = { position: "fixed" };
  switch (placement) {
    case "right":
      return {
        ...base,
        top: rect.top + rect.height / 2,
        left: rect.right + GAP,
        transform: "translateY(-50%)",
      };
    case "left":
      return {
        ...base,
        top: rect.top + rect.height / 2,
        left: rect.left - GAP,
        transform: "translate(-100%, -50%)",
      };
    case "top":
      return {
        ...base,
        top: rect.top - GAP,
        left: rect.left + rect.width / 2,
        transform: "translate(-50%, -100%)",
      };
    case "bottom":
      return {
        ...base,
        top: rect.bottom + GAP,
        left: rect.left + rect.width / 2,
        transform: "translateX(-50%)",
      };
  }
}

export const Tooltip = ({ content, placement = "right", children }: TooltipProps) => {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const [popupStyle, setPopupStyle] = useState<CSSProperties | null>(null);

  const handleMouseEnter = () => {
    if (!wrapperRef.current) return;
    setPopupStyle(getPopupStyle(wrapperRef.current.getBoundingClientRect(), placement));
  };

  const handleMouseLeave = () => setPopupStyle(null);

  return (
    <span
      ref={wrapperRef}
      className={styles.wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {popupStyle &&
        createPortal(
          <div className={styles.popup} data-placement={placement} style={popupStyle}>
            {content}
          </div>,
          wrapperRef.current?.ownerDocument.body ?? document.body,
        )}
    </span>
  );
};
